import * as PIXI from "pixi.js"
import PixiScene from "../PixiScene";
import EventApi from "../../../../../api/EventsApi";
import PixiCard from "./PixiCard";
import PixiSelector from "./PixiSelector";
import PixiType from "./PixiType";
import {CurrentGame, FieldState} from "../../../../../../types";
import PixiMessenger from "./PixiMessenger";
import PixiUtil from "./PixiUtil";
import Tiler from "./Tiler";
import MessageHelper from "../../../../../helpers/MessageHelper";
//@ts-ignore
import store from "../../../../../store";

interface ISize {
    width:  number;
    height: number;
}

interface IBackground extends ISize {
    color: string;
    image: string;
}

interface IField extends ISize {
    background: IBackground;
}

interface IPoint {
    x: number;
    y: number;
}

interface IStage extends IPoint, ISize {
    scale: IPoint;
}

export default class PixiTable extends PIXI.Container {

    private readonly scene: PixiScene;
    private sceneWidth:  number;
    private sceneHeight: number;
    private readonly maxScale: number = 1.5;
    private minScale: number = 1;
    private startScale: number = 1;
    private startPos: IPoint = { x:0, y:0 };
    private tablePos: IPoint | null = null;
    private thickness: number = 1;
    private bgContainer   = new PIXI.Container();
    private fgContainer   = new PIXI.Container();
    private cardContainer = new PIXI.Container();
    private statContainer = new PIXI.Container();
    private boxContainer  = new PIXI.Container();
    private box = new PIXI.Graphics();
    private boxRect = new PIXI.Rectangle;
    private adminHud: null|PIXI.Graphics = null;
    private adminGrid: null|PIXI.Graphics = null;
    private logoHud = new PIXI.Graphics();
    private logoSvg = new PIXI.Sprite(PIXI.Texture.WHITE);
    private showLogo: boolean = true;

    constructor(scene: PixiScene, config: any) {
        super();
        this.name = 'tableContainer';
        this.scene = scene;
        this.sceneWidth  = this.scene.rect!.width;
        this.sceneHeight = this.scene.rect!.height;
        this.addChild(this.bgContainer);
        this.addChild(this.fgContainer);
        this.addChild(this.statContainer);
        this.addChild(this.cardContainer);
        this.addChild(this.boxContainer);
        this.boxContainer.addChild(this.box);
        this.init(config);
    }

    public get stage(): IStage
    {
        return {
            x: this.x, y: this.y, scale: this.scale,
            width:  this.sceneWidth,
            height: this.sceneHeight
        };
    }

    public isDirty(oldGameField: IField, newGameField: IField): boolean
    {
        return ! oldGameField || ! newGameField
            || oldGameField.width  !== newGameField.width
            || oldGameField.height !== newGameField.height
            || oldGameField.background.width  !== newGameField.background.width
            || oldGameField.background.height !== newGameField.background.height
            || oldGameField.background.color !== newGameField.background.color
            || oldGameField.background.image !== newGameField.background.image;
    }

    /**
     * Инициализация
     */
    private init(config: any) {
        if (this.scene.rect) {
            this.scene.addInteractivity(this);
        }
    }

    /**
     * Установка камеры
     */
    private setStage(): void
    {
        if (this.scene && this.scene.gameField) {
            const field = this.scene.gameField;
            const stage = field.stage as IStage;

            const origScale = stage.scale.x;
            const kResize = Math.min(this.sceneWidth / stage.width, this.sceneHeight / stage.height);
            const scale = origScale * kResize;
            this.scale.set(scale);
            this.startScale = scale;
            this.thickness = 1 / scale;

            const fieldWidth = field.width  * this.scale.x, fieldHeight = field.height * this.scale.y;

            let newX: number, newY: number;
            if (fieldWidth < this.sceneWidth && fieldHeight < this.sceneHeight) {
                newX = this.sceneWidth  / 2 - fieldWidth  / 2;
                newY = this.sceneHeight / 2 - fieldHeight / 2;
            }
            else {
                const pos = {
                    x: (stage.x - stage.width /2) / origScale,
                    y: (stage.y - stage.height/2) / origScale
                };
                newX = pos.x * scale + this.sceneWidth /2;
                newY = pos.y * scale + this.sceneHeight/2;
            }
            this.startPos = this.clampPos(newX, newY);
            this.commitMove();
        }
    }

    /**
     * Создание спрайтов
     */
    private getSpriteFrom(spriteSrc: string): Promise<PIXI.Sprite>
    {
        const sprite = PIXI.Sprite.from(spriteSrc);
        const baseTexture = sprite.texture.baseTexture;
        return new Promise((resolve, reject) => {
            if (baseTexture.valid) {
                resolve(sprite)
            }
            else {
                baseTexture.on('loaded', () => resolve(sprite));
                baseTexture.on('error', (err) => {
                    console.warn(err.message);
                    reject(err);
                });
            }
        });
    }

    /**
     * Добавление картинки
     */
    private async addImg(url:string, x:number, y:number)
    {
        if (url) {
            const img = await this.getSpriteFrom(url);
            img.anchor.set(0.5);
            img.x = x;
            img.y = y;
            this.bgContainer.addChild(img);
        }
    }

    /**
     * Добавление паттерна
     */
    private async addPattern(url: string, size: ISize, repeat: string)
    {
        if (url) {
            const sprite = await this.getSpriteFrom(url);
            let pat: PIXI.Sprite, x = 0, y = 0;
            do {
                do {
                    pat = PIXI.Sprite.from(url);
                    pat.position.set(x, y)
                    this.bgContainer.addChild(pat);
                    x += sprite.width;
                }
                while (x < ('y' === repeat ? 0 : size.width));
                x = 0;
                y += sprite.height;
            }
            while (y < ('x' === repeat ? 0 : size.height));
        }
    }

    /**
     * Промис: добавление арта
     */
    private async addImgPromise(gameField: any)
    {
        const backgroundImage = gameField.background.image;
        const pattern = gameField.background.pattern ?? -1;
        if (pattern > 0) {
            let repeat = (2 === pattern)? 'x': (3 === pattern)? 'y': '';
            await this.addPattern(backgroundImage, gameField, repeat);
        }
        else {
            await this.addImg(backgroundImage,
                gameField.width  / 2, gameField.height / 2
            );
        }
    }

    /**
     * Отрисовка фона стола
     */
    public addBackground_TEST(newGameField?: FieldState): void
    {
        const gameField = newGameField ?? this.scene.gameField;
        this.createTiles(gameField)
            .then(cnt => {
                console.log('%c ### Tiles created:', 'color: #f0f', cnt);
                // this.addLogo(gameField);

                if (this.scene.isInAdminMode) {
                    this.addAdminDebug(gameField);
                }

                this.resetMinScale();
                this.resetZoom();
                // this.scene.tablePos = { x: this.x, y: this.y };
                this.scene.isLoaded = true;
                EventApi.triggerEvent(EventApi.TABLE_IS_LOADED, { stage: this.scene });
            });
    }


    public addBackground(newGameField?: FieldState): void
    {
        const gameField = newGameField ?? this.scene.gameField;
        this.showLogo = gameField.showLogo !== false;

        if (gameField && gameField.background) {
            // this.minScale = Math.max(this.sceneWidth / gameField.width, this.sceneHeight / gameField.height);
            this.bgContainer.removeChildren();

            const strColor = gameField.background.color;
            const numColor = parseInt(strColor.slice(1), 16);

            const bgRect = new PIXI.Graphics();
            bgRect.lineStyle(1, 0xFFFFFF);
            bgRect.beginFill(numColor || 0x35654d);
            bgRect.drawRect(0, 0, gameField.width, gameField.height);
            bgRect.endFill();
            this.bgContainer.addChild(bgRect);

            this.addImgPromise(gameField)
                .then(() => {
                    this.resetMinScale();
                    this.resetZoom();
                    this.addLogo(gameField);

                    // this.createTilesTest().then(() => {
                    //     console.log('%c ### Tiles created:', 'color: #f0f', 42);
                    // }).catch(e => console.warn(e.message));

                    setTimeout(() => {
                        // this.bgContainer.cacheAsBitmap = true;
                        EventApi.triggerEvent(EventApi.TABLE_IS_LOADED, { stage: this.scene });
                        this.scene.isLoaded = true;
                        const currentActivity: null|CurrentGame = store.getters.getCurrentActivity;
                        if (currentActivity) {
                            MessageHelper.loaderFinished({
                                'activityId': currentActivity.id
                            });
                        }
                    }, 500);
                })
                .catch(e => console.warn(e.message));
        }
    }

    private addLogo(gameField: any): void
    {
        const logoSvg = this.logoSvg = new PIXI.Sprite(PIXI.Texture.from('/images/context-menu/ic_logo.svg'));
        logoSvg.alpha = 0.1;
        logoSvg.anchor.set(0.5);
        logoSvg.scale.set(2);

        const logoPos = this.scene.getLogoPos();
        if (logoPos) {
            logoSvg.position.set(logoPos.x, logoPos.y);
        }
        else {
            const localPos = this.toLocal({
                x: this.scene.rect!.width/2,
                y: this.scene.rect!.height/2 - this.scene.handContainer.getHandHeight(true)/2
            }, void 0) as PIXI.Point;
            logoSvg.position.set(localPos.x, localPos.y);
            this.scene.setLogoPos(logoSvg.position.x, logoSvg.position.y);
        }

        const DEBUG = false;
        if (this.scene.isInAdminMode || DEBUG) {
            this.addAdminDebug(gameField);
            this.addDebugLogo(logoSvg.x, logoSvg.y);
            logoSvg.visible = this.showLogo;
        }
        if (DEBUG) {
            logoSvg.alpha = 1;
            logoSvg.tint = 0xff00ff;
        }
        this.fgContainer.addChild(logoSvg);
    }

    /**
     * Добавить отладочную сетку для админки
     * @param gameField
     */
    public addAdminDebug(gameField: FieldState): void
    {
        this.addAdminGrid(gameField.width, gameField.height);
        this.addAdminHUD(gameField.width, gameField.height);
    }

    /**
     * Показать/скрыть отладочную сетку админки
     */
    public changeAdminDebugVisibility(value: boolean): void
    {
        // this.bgContainer.cacheAsBitmap = false;
        if (this.adminHud) {
            this.adminHud.visible = value;
        }
        if (this.adminGrid) {
            this.adminGrid.visible = value;
        }
        // this.bgContainer.cacheAsBitmap = true;
    }

    /**
     * Зум стола
     */
    public tableChangeZoom(mode: string, wheel?: { x:number, y: number }): void
    {
        if ('*' === mode) {
            this.resetZoom();
            return;
        }
        const buttonScale = 1.15, wheelScale = 1.03;
        const kScale = wheel ? wheelScale : buttonScale;
        let scale = this.scale.x;
        if ('+' === mode) {
            scale *= kScale;
        }
        else if ('-' === mode) {
            scale /= kScale;
        }
        this.setZoom(scale, wheel);
    }

    /**
     * Ограничения масштаба
     */
    private clampScale(scale: number): number
    {
        return PixiUtil.clamp(this.minScale, scale, this.maxScale);
    }

    public resizeRect(oldStage:IStage, oldRect: DOMRect, newRect: DOMRect): void
    {
        if (newRect && newRect.width && newRect.height) {
            const oldScale = oldStage.scale.x;
            const kResize = Math.min(newRect.width / oldRect.width, newRect.height / oldRect.height);
            let newScale = oldStage.scale.x * kResize;
            newScale = this.clampScale(newScale);
            this.scale.set(newScale);

            let worldPos = {
                x: (oldRect.width/2  - oldStage.x) / oldScale,
                y: (oldRect.height/2 - oldStage.y) / oldScale,
            };
            const newX = newRect.width/2  - worldPos.x * newScale;
            const newY = newRect.height/2 - worldPos.y * newScale;
            this.clampPos(newX, newY);
        }
    }

    /**
     * Установить зум
     */
    public setZoom(scale: number, wheel?: { x:number, y: number }): void
    {
        scale = this.clampScale(scale);
        const oldScale = this.scale.x;
        this.scale.set(scale);

        let pointX = wheel ? wheel.x : (this.sceneWidth  / 2);
        let pointY = wheel ? wheel.y : (this.sceneHeight / 2);

        let worldPos = {
            x: (pointX - this.x) / oldScale,
            y: (pointY - this.y) / oldScale,
        };
        const newX = pointX - worldPos.x * scale;
        const newY = pointY - worldPos.y * scale;
        this.clampPos(newX, newY);
    }

    /**
     * Восстановить зум
     */
    public resetZoom(): void
    {
        this.setStage();
        this.tableChangeZoom('');

        const newX = this.startPos.x;
        const newY = this.startPos.y;
        this.clampPos(newX, newY);
    }

    /**
     * Минимальный зум
     */
    public minZoom(): void
    {
        this.scale.set(this.minScale);
        // this.scale.set(300 / this.scene.cardSize.height);

        const newX = (this.sceneWidth  - this.scene.gameField.width  * this.scale.x) / 2;
        const newY = (this.sceneHeight - this.scene.gameField.height * this.scale.y) / 2;
        this.clampPos(newX, newY);
    }

    /**
     * Привести слой для переноса в соответствие со столом
     */
    public adjustTop(): void
    {
        this.scene.topContainer.scale.set(this.scale.x);
        this.scene.topContainer.x = this.x;
        this.scene.topContainer.y = this.y;
    }

    /**
     * Перемещение стола
     */
    public move(x: number, y: number): void {
        this.cursor = 'move';
        this.clampPos(x, y);
    }

    public commitMove(): void {
        if (this.tablePos) {
            this.x = this.tablePos.x;
            this.y = this.tablePos.y;
            this.adjustTop();
            this.tablePos = null;
        }
    }

    /**
     * Ограничить перемещение стола экраном
     * @private
     */
    private clampPos(x: number, y: number)
    {
        let newX = 0, newY = 0;
        const gameField = this.scene.gameField;
        if (gameField) {
            const fieldWidth = gameField.width  * this.scale.x, fieldHeight = gameField.height * this.scale.y;
            const limX = -fieldWidth  + this.sceneWidth;
            const limY = -fieldHeight + this.sceneHeight;

            if (fieldWidth < this.sceneWidth && fieldHeight < this.sceneHeight) {
                newX = PixiUtil.clamp(0, x, limX);
                newY = PixiUtil.clamp(0, y, limY);
            }
            else {
                newX = PixiUtil.clamp(limX, x, 0);
                newY = PixiUtil.clamp(limY, y, 0);
            }
            //
            // this.x = newX;
            // this.y = newY;
            // this.adjustTop();
            //
            this.tablePos = { x: newX, y: newY };
            this.commitMove();
        }
        return { x: newX, y: newY };
    }
    private clampPos_OLD(): void
    {
        const gameField = this.scene.gameField;
        if (gameField) {
            const fieldWidth = gameField.width  * this.scale.x, fieldHeight = gameField.height * this.scale.y;
            const limX = -fieldWidth  + this.sceneWidth;
            const limY = -fieldHeight + this.sceneHeight;

            if (fieldWidth < this.sceneWidth && fieldHeight < this.sceneHeight) {
                this.x = PixiUtil.clamp(0, this.x, limX);
                this.y = PixiUtil.clamp(0, this.y, limY);
            }
            else {
                this.x = PixiUtil.clamp(limX, this.x, 0);
                this.y = PixiUtil.clamp(limY, this.y, 0);
            }
        }
        this.adjustTop();
    }

    /**
     * Добавить карту на стол
     * @param card
     * @param isStatic
     */
    public addCard(card: PIXI.Container, isStatic = false): void
    {
        if (card) {
            if (isStatic) {
                this.statContainer.addChild(card);
            }
            else {
                this.cardContainer.addChild(card);
            }
        }
    }

    /**
     * Удалить карту со стола
     * @param card
     */
    public removeCard(card: PIXI.Container): void
    {
        if (card) {
            const parent = card.parent as PIXI.Container;
            if (parent) {
                parent.removeChild(card);
            }
        }
    }

    /**
     * Отладка
     */
    public info(): void
    {
        // console.log('%c ### PixiTable::info', 'color: #f0f', this.scale.x, this.x, this.y);
        console.log('%c ### PixiTable::info', 'color: #f0f', this.statContainer.children.length, this.cardContainer.children.length);
    }

    /**
     * HUD для отладки:
     */
    private addAdminHUD(width:number, height:number): void
    {
        this.adminHud = new PIXI.Graphics();
        this.adminHud.lineStyle(2 * this.thickness, 0x33ff00, 0.5);
        this.adminHud.drawRect(width/2- 50, height/2- 50, 100, 100);
        this.adminHud.drawRect(50, 5, width - 100, height - 100);

        this.adminHud.moveTo(0, height/2); this.adminHud.lineTo(width, height/2);
        this.adminHud.moveTo(width/2, 0); this.adminHud.lineTo(width/2, height);
        this.bgContainer.addChild(this.adminHud);
    }

    /**
     * Рамка лого для отладки:
     */
    private addDebugLogo(x:number, y:number): void
    {
        const logoW = 200, logoH = 80;
        this.logoHud = new PIXI.Graphics();
        this.logoHud.lineStyle(4, 0xffffff, 1);
        this.logoHud.drawRect(-logoW, -logoH, 2*logoW, 2*logoH);
        this.logoHud.moveTo(-logoW, -logoH); this.logoHud.lineTo(logoW,  logoH);
        this.logoHud.moveTo(-logoW,  logoH); this.logoHud.lineTo(logoW, -logoH);
        this.logoHud.position.set(x, y);
        this.logoHud.visible = !this.showLogo;
        this.fgContainer.addChild(this.logoHud);
    }

    /**
     * Grid для отладки:
     */
    public addAdminGrid(width:number, height:number): void
    {
        this.adminGrid = new PIXI.Graphics();
        const cellSize = { width: 50, height: 50 };
        this.adminGrid.lineStyle(this.thickness, 0xffffff, 0.25);
        for (let i = 0; i < width / cellSize.width; i++) {
            this.adminGrid.moveTo(i * cellSize.width, 0); this.adminGrid.lineTo(i * cellSize.width, height);
        }
        for (let i = 0; i < height / cellSize.height; i++) {
            this.adminGrid.moveTo(0, i * cellSize.height); this.adminGrid.lineTo(width, i * cellSize.height);
        }
        this.bgContainer.addChild(this.adminGrid);
    }

    /**
     * Работа с областью выделения [*Box]:
     */
    public clearBox(): void
    {
        this.box.clear();
    }

    public startBox(pos: PIXI.Point): void
    {
        this.boxRect.x = pos.x;
        this.boxRect.y = pos.y;
    }

    public drawBox(pos: PIXI.Point): void
    {
        this.boxRect.width  = pos.x - this.boxRect.x;
        this.boxRect.height = pos.y - this.boxRect.y;
        this.box.clear();
        this.box.lineStyle(1, 0xffffff, 0.75);
        this.box.beginFill(0xffffff, 0.25);
        this.box.drawRect(this.boxRect.x, this.boxRect.y, this.boxRect.width, this.boxRect.height);
        this.box.endFill();
    }

    public selectBox(): void
    {
        const rect = this.boxRect;
        if (this.boxRect.width < 0) {
            rect.x = this.boxRect.x + this.boxRect.width;
            rect.width = -this.boxRect.width;
        }
        if (this.boxRect.height < 0) {
            rect.y = this.boxRect.y + this.boxRect.height;
            rect.height = -this.boxRect.height;
        }

        const pileList: Array<number> = [];
        this.cardContainer.children.forEach(it => {
            const card = it as PixiCard;
            if (card.objType === PixiType.GAME_OBJ_CARD) {
                if (!card.blurred
                    && !card.locked
                    && !card.pileZ
                    && !card.parentPile?.inPermanentLock()
                    // && rect.contains(card.x, card.y)
                    && card.points.some(point => rect.contains(point.x, point.y))
                ) {
                    pileList.push(card.pileId);
                    PixiSelector.add(card, true, false, true);
                    card.setFilter(this.scene.outlineFilter);
                }
            }
        });

        if (pileList.length) {
            PixiMessenger.sendDisableCardMessage(pileList, false);
        }

        this.boxRect.width  = 0;
        this.boxRect.height = 0;
    }

    /**
     * Свернуть для перемещения
     */
    public zip(): void
    {
        try {
            // this.cardContainer.cacheAsBitmap = true;
        }
        catch (e) {
            console.warn(e.message);
        }
    }

    /**
     * Развернуть после перемещения
     */
    public unzip(): void
    {
        // this.cardContainer.cacheAsBitmap = false;
    }

    /**
     * Изменить мин масштаб после переключения Руки
     * @private
     */
    private resetMinScale(newGameField?: FieldState): void
    {
        const gameField = newGameField ?? this.scene.gameField;
        this.minScale = Math.max(this.sceneWidth / gameField.width, this.sceneHeight / gameField.height);
        this.scale.set(this.clampScale(this.scale.x));
        this.clampPos(this.x, this.y);
    }

    /**
     * Изменить высоту экрана после переключения Руки
     */
    public resetHeight(height: number): void
    {
        this.sceneHeight = height;
        this.resetMinScale();
    }

    private async createTiles(gameField: FieldState) {
        console.log('%c ### >>>', 'color: #f0f', gameField);
        if (!gameField || !gameField.width || !gameField.height) {
            return 0;
        }
        try {
            const tiler = new Tiler(gameField.width, gameField.height, gameField.background.color);
            if (gameField.background.image) {
                if (null === gameField.background.pattern) {
                    await tiler.drawImage0(gameField.background.image);
                }
                else {
                    await tiler.drawPattern(gameField.background.image);
                }
            }
            const tiles = tiler.createTiles(4096);

            let cnt = 0;
            tiles.forEach((tilesRow, row) => {
                tilesRow.forEach((tile, col) => {
                    this.bgContainer.addChild(tile);
                    // const sizeX = tile.width + 8, sizeY = tile.height + 8;
                    // tile.position.set(sizeX * col, sizeY * row);
                    tile.position.set(tile.width * col, tile.height * row);
                    cnt++;
                })
            });
            return cnt;
        }
        catch (e) {
            console.warn(e.message);
            return 0;
        }
    }

    private async createTilesTest() {
        try {
            const tiler = new Tiler(3000, 3000, '#369');
            await tiler.drawPattern('https://placekitten.com/250/250');
            await tiler.drawImage0('https://placekitten.com/500/500', 3.5);
            // await tiler.drawImage('https://placekitten.com/500/500', 1000, 1000, 2);

            const tiles = tiler.createTiles(650);

            let cnt = 0;
            tiles.forEach((tilesRow, row) => {
                tilesRow.forEach((tile, col) => {
                    this.scene.topContainer.addChild(tile);
                    // const sizeX = tile.width  + 8;
                    // const sizeY = tile.height + 8;
                    // tile.position.set(sizeX * col, sizeY * row);
                    tile.position.set(tile.width * col, tile.height * row);
                    cnt++;
                })
            });
            return cnt;
        }
        catch (e) {
            console.warn(e.message);
            return 0;
        }
    }

    /**
     * Не отрисовывать элементы за столом
     */
    public hideOffScreenItems(): void
    {
        this.statContainer.children.forEach(it => {
            this.scene.hideOffScreenItem(it);
        });
        this.cardContainer.children.forEach(it => {
            this.scene.hideOffScreenItem(it);
        });
    }

    /**
     * Переключение лого в админке
     */
    public toggleLogo(value: boolean): void
    {
        if (this.scene.isInAdminMode) {
            this.logoSvg.visible = value;
            this.logoHud.visible = !value;
        }
    }

    /**
     * Карты на столе
     */
    public showSimiloButtons(value: boolean): void
    {
        const tableCards = this.cardContainer.children;
        tableCards.forEach(it => {
            const card = it as PixiCard;
            if (card && card.isTopCard && !card.locked) {
                card.showSimiloButton(value);
            }
            if (card && card.art) {
                setTimeout(() => {
                    card.art.filters = this.scene.isSimiloCardSelected(card.cardId) ? [ card.outlineFilter ] : [];
                }, 200);
            }
        });
    }

}
